import apiClient from './apiClient';
import config from '../utils/config';

/**
 * Service for location-related API calls
 */
const locationService = {
  /**
   * Get all locations for a project
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the list of locations
   */
  getLocations: async (projectId) => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/locations`);
      console.log('Locations response:', response);
      return response.data?.locations || [];
    } catch (error) {
      console.error('Error fetching locations:', error);
      return [];
    }
  },

  /**
   * Get a single location
   * @param {string} projectId - Project ID
   * @param {string} locationId - Location ID
   * @returns {Promise} Promise resolving to the location details
   */
  getLocation: async (projectId, locationId) => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/locations/${locationId}`);
      return response.data?.location;
    } catch (error) {
      console.error('Error fetching location:', error);
      throw error;
    }
  },

  /**
   * Create a new location
   * @param {string} projectId - Project ID
   * @param {Object} data - Location data
   * @returns {Promise} Promise resolving to the created location
   */
  createLocation: async (projectId, data) => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/locations`, data);
      return response.data?.location;
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  },

  /**
   * Update a location
   * @param {string} projectId - Project ID
   * @param {string} locationId - Location ID
   * @param {Object} data - Updated location data
   * @returns {Promise} Promise resolving to the updated location
   */
  updateLocation: async (projectId, locationId, data) => {
    try {
      const response = await apiClient.patch(`/projects/${projectId}/locations/${locationId}`, data);
      return response.data?.location;
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  },

  /**
   * Delete a location
   * @param {string} projectId - Project ID
   * @param {string} locationId - Location ID
   * @returns {Promise} Promise resolving to the deletion status
   */
  deleteLocation: async (projectId, locationId) => {
    try {
      const response = await apiClient.delete(`/projects/${projectId}/locations/${locationId}`);
      return response;
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  },

  /**
   * Find nearest locations
   * @param {string} projectId - Project ID
   * @param {number} latitude - User latitude
   * @param {number} longitude - User longitude
   * @param {Object} options - Additional options (limit, type)
   * @returns {Promise} Promise resolving to the nearest locations
   */
  findNearestLocations: async (projectId, latitude, longitude, options = {}) => {
    try {
      const { limit = 3, type } = options;
      const params = {
        latitude,
        longitude,
        limit
      };
      
      if (type) {
        params.type = type;
      }
      
      const response = await apiClient.get(`/projects/${projectId}/locations/nearest`, { params });
      return response.data;
    } catch (error) {
      console.error('Error finding nearest locations:', error);
      throw error;
    }
  },

  /**
   * Bulk import locations
   * @param {string} projectId - Project ID
   * @param {Array} locations - Array of location objects
   * @returns {Promise} Promise resolving to the created locations
   */
  bulkImportLocations: async (projectId, locations) => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/locations/bulk`, { locations });
      return response.data?.locations;
    } catch (error) {
      console.error('Error bulk importing locations:', error);
      throw error;
    }
  },

  /**
   * Get location by address using geocoding
   * @param {string} address - Address to geocode
   * @param {string} apiKey - Google Maps API key
   * @returns {Promise} Promise resolving to the geocoded location
   */
  geocodeAddress: async (address, apiKey) => {
    try {
      // This should be done server-side in a production app
      // We're doing it client-side for demonstration purposes
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const result = data.results[0];
        const { lat, lng } = result.geometry.location;
        
        return {
          formatted_address: result.formatted_address,
          coordinates: {
            latitude: lat,
            longitude: lng
          }
        };
      }
      
      throw new Error(`Geocoding failed: ${data.status}`);
    } catch (error) {
      console.error('Error geocoding address:', error);
      throw error;
    }
  }
};

export default locationService; 